import { useState, useMemo, useEffect } from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import { LogoLink } from '@/components/LogoLink';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent,
  IonButton, IonCard, IonCardContent, IonCardHeader, IonCardTitle,
  IonIcon,
} from '@ionic/react';
import { list, notifications, briefcase, sparkles } from 'ionicons/icons';
import { useAuthStore } from '@/stores/useAuthStore';
import {
  useMyCandidateProfile, useMyCandidateProfileStrength, useLatestVerification,
  useCandidateExperience, useCandidateSkills,
  useCandidateNpsAggregate, useActiveSurvey, useCandidateNpsMonthly,
  useCandidateAssociations, useCandidateBadges,
} from '@/lib/api/hooks';
import { useUIStore } from '@/stores/useUIStore';
import { NoindexSeo } from '@/components/NoindexSeo';
import { ProfileHeader } from '@/components/candidate/ProfileHeader';
import { ProfileStrength } from '@/components/candidate/ProfileStrength';
import { BadgeShelf } from '@/components/candidate/BadgeShelf';
import { StaleProfileBanner } from '@/components/candidate/StaleProfileBanner';
import { GetStartedQuest, type QuestStepId } from '@/components/candidate/GetStartedQuest';
import { ProfileTabs, type ProfileTab } from '@/components/candidate/ProfileTabs';
import { SkillManagerContent } from '@/components/candidate/SkillManager';
import { ProfessionalMembershipsPanel } from '@/components/candidate/ProfessionalMembershipsPanel';
import { NpsDashboardCard } from '@/components/candidate/NpsDashboardCard';
import { NpsHistoryList } from '@/components/candidate/NpsHistoryList';
import { NpsInsightsPanel } from '@/components/candidate/NpsInsightsPanel';
import { PreferencesPanel } from '@/components/candidate/PreferencesPanel';
import { SkeletonDashboard } from '@/components/candidate/SkeletonLoaders';
import { AppFooter } from '@/components/AppFooter';
import { MatchSuggestions } from './MatchSuggestions';
import { SponsorOrderHistory } from './SponsorOrderHistory';
import { ProfileCover, HERO_IMAGES } from './ProfileCover';
import { AccountInfo } from '@/components/candidate/AccountInfo';
import { ProfileEditModal } from '@/components/candidate/ProfileEditModal';
import { ProfileEditPanel } from '@/components/candidate/ProfileEditPanel';
import { VerifyIdentityPanel } from '@/components/candidate/VerifyIdentityPanel';
import './CandidateDashboard.css';

const VALID_TABS: ProfileTab[] = ['overview', 'skills', 'experience', 'preferences', 'memberships', 'nps'];
const TAB_ROUTES: Record<ProfileTab, string> = {
  overview: '/profile',
  skills: '/profile/skills',
  experience: '/profile/experience',
  preferences: '/profile/preferences',
  memberships: '/profile/memberships',
  nps: '/profile/nps',
};

function tabFromPath(pathname: string): ProfileTab {
  for (const tab of VALID_TABS) {
    if (tab === 'overview' && pathname === '/profile') return tab;
    if (tab !== 'overview' && pathname === TAB_ROUTES[tab]) return tab;
  }
  return 'overview';
}

interface DashboardProps {
  profile: import('@/types').CandidateProfile | null;
}

export function CandidateDashboard({ profile: profileProp }: DashboardProps) {
  const role = useAuthStore((s) => s.roleSelection) ?? 'candidate';
  const userName = useAuthStore((s) => s.profile?.name);
  const history = useHistory();
  const { pathname, search } = useLocation();
  const activeTab = tabFromPath(pathname);

  // M09 S02 T2.1/T2.2 — signup lands here with ?welcome=1; force the quest
  // once, then strip the param so refreshes behave like a normal visit.
  const [questForceShow] = useState(() => new URLSearchParams(search).get('welcome') === '1');
  useEffect(() => {
    if (questForceShow) history.replace('/profile');
  }, [questForceShow, history]);

  const handleQuestStep = (step: QuestStepId) => {
    if (step === 'photo' || step === 'headline-bio') history.push('/profile/edit');
    else if (step === 'skills') history.push(TAB_ROUTES.skills);
    else if (step === 'experience') history.push(TAB_ROUTES.experience);
    else if (step === 'preferences') history.push(TAB_ROUTES.preferences);
    else if (step === 'verify') history.push('/profile/verify-identity');
  };

  const { data: myProfileEnvelope, isLoading: profileLoading } = useMyCandidateProfile();
  const profile = profileProp ?? myProfileEnvelope?.data ?? null;

  const candidateId = profile?._id ?? '';

  const { data: strengthEnvelope } = useMyCandidateProfileStrength();
  const { data: verificationEnvelope } = useLatestVerification(candidateId);
  const { data: badgeEnvelope } = useCandidateBadges();

  // M09 S03: celebrate tier improvements exactly once (localStorage guard —
  // plain reloads never re-fire; only an upward tier change toasts).
  const strengthTier = strengthEnvelope?.data?.tier;
  useEffect(() => {
    if (!strengthTier) return;
    const order = ['Starter', 'Bronze', 'Silver', 'Gold'];
    try {
      const prev = localStorage.getItem('bp-last-tier');
      if (prev && order.indexOf(strengthTier) > order.indexOf(prev)) {
        useUIStore.getState().addToast({ message: `Tier up — ${strengthTier}!`, type: 'success' });
      }
      localStorage.setItem('bp-last-tier', strengthTier);
    } catch {
      /* storage unavailable — skip celebration */
    }
  }, [strengthTier]);

  const { data: skillsEnvelope, isLoading: skillsLoading } = useCandidateSkills(candidateId);
  const skills = skillsEnvelope?.data ?? [];
  const { data: apiExperience, isLoading: expLoading } = useCandidateExperience(candidateId);
  const { data: associationsEnvelope } = useCandidateAssociations(candidateId);
  const memberships = associationsEnvelope?.data ?? [];

  const { data: npsEnvelope } = useCandidateNpsAggregate(candidateId);
  const npsAggregate = npsEnvelope?.data;
  const { data: activeSurveyEnvelope } = useActiveSurvey('candidateprofile');
  const activeSurveyId = activeSurveyEnvelope?.data?._id ?? '';
  const { data: npsMonthlyEnvelope } = useCandidateNpsMonthly(activeSurveyId);
  const npsMonthly = npsMonthlyEnvelope?.data ?? [];

  const isLoading = profileLoading || skillsLoading || expLoading;

  const [editProfileOpen, setEditProfileOpen] = useState(false);
  const [verifyIdentityOpen, setVerifyIdentityOpen] = useState(false);

  const badges = useMemo(() => ({
    skills: skills.length || undefined,
    experience: apiExperience?.length || undefined,
    memberships: memberships.length || undefined,
    nps: npsAggregate && npsAggregate.total > 0 ? 1 : undefined,
  }), [skills, apiExperience, memberships, npsAggregate]);

  if (role === 'employer') {
    return <Redirect to="/employer" />;
  }

  if (isLoading) {
    return (
      <IonPage>
        <NoindexSeo />
        <IonHeader>
          <IonToolbar>
            <IonTitle><LogoLink style={{ height: '28px', marginTop: '4px' }} /></IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <SkeletonDashboard />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <LogoLink style={{ height: '28px', marginTop: '4px' }} />
          </IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding" style={{ paddingTop: 0 }}>
        {profile ? (
          <>
            <ProfileCover profile={profile} userName={userName} />

            <ProfileHeader
              profile={profile}
              npsAggregate={npsAggregate}
            />

            <div style={{ padding: '0 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <StaleProfileBanner
                lastUpdatedAt={strengthEnvelope?.data?.lastUpdatedAt}
                score={strengthEnvelope?.data?.score}
              />
              <GetStartedQuest
                strength={strengthEnvelope?.data}
                forceShow={questForceShow}
                onStepClick={handleQuestStep}
              />
              <ProfileStrength
                strength={strengthEnvelope?.data}
                profile={profile}
                skillsCount={skills.length}
                experienceCount={apiExperience?.length ?? 0}
                verified={verificationEnvelope?.data?.status === 'Verified'}
                onSectionClick={(section) => {
                  if (['headline', 'bio', 'photo', 'salary', 'industry', 'locations'].includes(section)) {
                    history.push('/profile/edit');
                  } else if (section === 'skills') {
                    history.push(TAB_ROUTES.skills);
                  } else if (section === 'experience') {
                    history.push(TAB_ROUTES.experience);
                  } else if (section === 'preferences') {
                    history.push(TAB_ROUTES.preferences);
                  } else if (section === 'verified') {
                    setVerifyIdentityOpen(true);
                  }
                }}
              />
              <BadgeShelf badges={badgeEnvelope?.data} />
            </div>

            <div style={{ marginTop: 8 }}>
              <ProfileTabs
                activeTab={activeTab}
                onTabChange={(tab) => history.push(TAB_ROUTES[tab])}
                badges={badges}
              />
            </div>

            <div style={{ padding: '12px 0' }}>
              {activeTab === 'overview' && (
                <>
                  <AccountInfo
                    onEditProfile={() => setEditProfileOpen(true)}
                    onVerifyIdentity={() => setVerifyIdentityOpen(true)}
                  />

                  {npsAggregate && npsAggregate.total > 0 && (
                    <div style={{ padding: '0 4px' }}>
                      <NpsDashboardCard candidateId={candidateId} />
                    </div>
                  )}

                  <IonCard>
                    <IonCardHeader>
                      <IonCardTitle style={{ color: '#f1f5f9' }}>
                        <IonIcon icon={notifications} style={{ color: '#60a5fa' }} /> Match activity
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <p style={{ color: 'var(--bp-text-muted)', fontSize: '0.85rem', margin: 0 }}>
                        No new views or shortlists yet. We'll notify you when an employer shows interest.
                      </p>
                    </IonCardContent>
                  </IonCard>

                  {/* M07 S06 T6.5 — sponsor order history */}
                  <SponsorOrderHistory />

                  <IonCard>
                    <IonCardHeader>
                      <IonCardTitle style={{ color: '#f1f5f9' }}>
                        <IonIcon icon={briefcase} style={{ color: '#60a5fa' }} /> Job suggestions
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <MatchSuggestions candidateSlug={profile.slug ?? ''} />
                    </IonCardContent>
                  </IonCard>
                </>
              )}

              {activeTab === 'skills' && (
                <SkillManagerContent candidateId={candidateId} />
              )}

              {activeTab === 'experience' && (
                <div style={{ padding: '0 16px' }}>
                  <IonButton expand="block" fill="outline" onClick={() => history.push('/profile/experience')}>
                    <IonIcon icon={list} slot="start" />
                    {apiExperience && apiExperience.length > 0 ? 'Manage experience' : 'Add your first role'}
                  </IonButton>
                </div>
              )}

              {activeTab === 'preferences' && (
                <div style={{ padding: '0 4px' }}>
                  <PreferencesPanel />
                </div>
              )}

              {activeTab === 'memberships' && (
                <div style={{ padding: '0 4px' }}>
                  <ProfessionalMembershipsPanel legalEntityId={profile._id} />
                </div>
              )}

              {activeTab === 'nps' && (
                <div style={{ padding: '0 4px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {npsAggregate && npsAggregate.total > 0 && (
                    <>
                      <NpsDashboardCard candidateId={candidateId} />
                      <NpsInsightsPanel aggregate={npsAggregate} />
                      <NpsHistoryList monthly={npsMonthly} />
                    </>
                  )}
                  {!npsAggregate || npsAggregate.total === 0 ? (
                    <div style={{
                      padding: '24px 16px', textAlign: 'center',
                      color: 'var(--bp-text-muted)',
                      fontSize: '0.85rem',
                    }}>
                      No NPS ratings yet. Share your profile to get feedback from employers.
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </>
        ) : (
          <ProfileHero
            onGetStarted={() => history.push('/register')}
            onSignIn={() => history.push('/signin')}
          />
        )}

        <AppFooter />
      </IonContent>

      <ProfileEditModal open={editProfileOpen} onClose={() => setEditProfileOpen(false)} title="Edit Profile">
        <ProfileEditPanel onSaved={() => setEditProfileOpen(false)} />
      </ProfileEditModal>

      <ProfileEditModal open={verifyIdentityOpen} onClose={() => setVerifyIdentityOpen(false)} title="Verify Identity">
        <VerifyIdentityPanel onDone={() => setVerifyIdentityOpen(false)} />
      </ProfileEditModal>
    </IonPage>
  );
}

function ProfileHero({ onGetStarted, onSignIn }: { onGetStarted: () => void; onSignIn: () => void }) {
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setHeroIdx((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    HERO_IMAGES.slice(0, 3).forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  return (
    <div className="cd-guest-hero">
      <div className="cd-guest-hero-bg" aria-hidden="true">
        {HERO_IMAGES.map((src, i) => (
          <div
            key={src}
            className="cd-guest-hero-slide"
            style={{
              backgroundImage: `url('${src}')`,
              opacity: i === heroIdx ? 1 : 0,
              transform: i === heroIdx ? 'scale(1.08)' : 'scale(1)',
            }}
          />
        ))}
        <div className="cd-guest-hero-overlay" />
      </div>
      <div className="cd-guest-hero-glow cd-guest-hero-glow-1" />
      <div className="cd-guest-hero-glow cd-guest-hero-glow-2" />

      <div className="cd-guest-hero-content">
        <div className="cd-guest-badge">
          <IonIcon icon={sparkles} style={{ fontSize: '0.625rem' }} />
          South Africa's Skills-First Marketplace
        </div>

        <h1 className="cd-guest-heading">
          Your Skills Deserve{' '}
          <span className="cd-guest-heading-accent">
            Better Work
          </span>
        </h1>

        <p className="cd-guest-subheading">
          BluePages connects skilled tradespeople with homeowners and businesses
          across South Africa. Build your verified profile, get matched by AI,
          and land your next contract — all from your phone. We'd love to have
          you join our growing community.
        </p>

        <div className="cd-guest-actions">
          <button className="cd-guest-cta" onClick={onGetStarted}>
            Get Started — It's Free
          </button>
          <button className="cd-guest-cta-secondary" onClick={onSignIn}>
            Already have an account? Sign in
          </button>
        </div>

        <div className="cd-guest-trust">
          <span>✓ Verified IDs</span>
          <span>✓ POPIA compliant</span>
          <span>✓ AI matching</span>
        </div>
      </div>
    </div>
  );
}
